import { InfoIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Empty } from '@/components/lemmala-design-system/empty/empty'
import { Skeleton } from '@/components/lemmala-design-system/skeleton/skeleton'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import { WIDGET_CARD_SKELETON_ROWS } from './consts'

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded border bg-main-background-card text-card-foreground shadow-sm',
        className,
      )}
      {...props}
    />
  ),
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-2', className)} {...props} />
  ),
)
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-lg font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
)
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ ...props }, ref) => <div ref={ref} {...props} />,
)
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  ),
)
CardFooter.displayName = 'CardFooter'

type WidgetCardProps = {
  title: string
  description?: string
  info?: string
  headerAction?: React.ReactNode
  footer?: React.ReactNode
  isPending?: boolean
  isEmpty?: boolean
  emptyTitle?: string
  emptySubtitle?: string
  className?: string
  children: React.ReactNode
}

const WidgetCard = ({
  title,
  description,
  info,
  headerAction,
  footer,
  isPending = false,
  isEmpty = false,
  emptyTitle,
  emptySubtitle,
  className,
  children,
}: WidgetCardProps) => (
  <div
    className={cn(
      'flex flex-col gap-4 rounded-lg border border-main-border-default bg-main-background-card py-5',
      className,
    )}
  >
    {isPending ? (
      <div className='flex flex-1 flex-col items-start gap-8 px-5'>
        <Skeleton className='h-4 w-36' />
        {Array.from({ length: WIDGET_CARD_SKELETON_ROWS }).map((_, index) => (
          <div key={index} className='flex w-full items-center justify-between'>
            <Skeleton className='h-4 w-56' />
            <Skeleton className='h-4 w-20' />
          </div>
        ))}
      </div>
    ) : (
      <>
        <div className='flex items-start justify-between gap-2 pl-5 pr-2'>
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-center gap-1.5'>
              <h3 className='truncate text-main-foreground-primary text-heading-xsmall'>{title}</h3>
              {info && (
                <Tooltip
                  trigger={
                    <InfoIcon
                      aria-label={info}
                      className='size-3.5 shrink-0 text-main-foreground-tertiary'
                    />
                  }
                >
                  {info}
                </Tooltip>
              )}
            </div>
            {description && (
              <p className='text-main-foreground-tertiary text-body-small'>{description}</p>
            )}
          </div>
          {headerAction}
        </div>
        <div className='flex flex-1 flex-col'>
          {isEmpty ? (
            <Empty className='my-auto' title={emptyTitle} subtitle={emptySubtitle} />
          ) : (
            children
          )}
        </div>
        {footer && <div className='px-5'>{footer}</div>}
      </>
    )}
  </div>
)

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, WidgetCard }
